import { Injectable, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { AuthService } from '../../../services/auth.service';
import { ApiService } from '../../../services/api.service';
import { AI_SYSTEM_MESSAGES } from '../ai-chat.constant';
import { ProfileService } from '../../../services/profile.service';
import { ChatSessionService } from './chat-session.service';
import { ChatMessageService } from './chat-message.service';
import { ChatDocumentService } from './chat-document.service';
import { ChatRole, RoutePath } from '../../../enums/api.enum';
import { Router } from '@angular/router';
import { AutomationStateService } from './automation-state.service';
import { PromptLibraryService } from './prompt-library.service';
import { GENERIC_ERROR_MESSAGE } from "../../../constants/api.constant";
import { ChatUtilitiesService } from "./chat-utilities.service";
import { AutomationExecutionService } from "./automation-execution.service";
import { firstValueFrom } from 'rxjs';
import { AlertService } from '../../../shared/alert/alert.service';
import { AlertEnum } from '../../../shared/alert/alert.enum';
import { UiLayoutService } from '../../../ui-layout/services/ui-layout.service';
import { MultiSessionService } from '../../../services/multi-session.service';
import { SessionStatus } from '../../../enums/session-status.enum';
import { ExecutionStatusEnum } from '../../../enums/automation.enum';
import { UtilitiesService } from '../../../services/utilities.service';

@Injectable({
  providedIn: 'root',
})
export class AiChatStreamingService {
  // Keep track of the last selected session ID for cases where we might fallback to it, 
  // but prefer passing sessionId explicitly
  #selectedSessionId: string | number = '';

  #authService = inject(AuthService);
  #multiSessionService = inject(MultiSessionService);
  #apiService = inject(ApiService);
  #alertService = inject(AlertService);
  #router = inject(Router);
  #profileService = inject(ProfileService);
  #chatSessionService = inject(ChatSessionService);
  #chatMessageService = inject(ChatMessageService);
  #chatDocumentService = inject(ChatDocumentService);
  #automationStateService = inject(AutomationStateService);
  #automationExecutionService = inject(AutomationExecutionService);
  #promptLibraryService = inject(PromptLibraryService);
  #chatUtilitiesService = inject(ChatUtilitiesService);
  #uiLayoutService = inject(UiLayoutService);
  #utilitiesService = inject(UtilitiesService);

  /**
   * Sends a message using streaming and handles the response progressively.
   * @param content - The message content to send
   * @param chatRole - The role of the sender (optional)
   * @returns Promise<boolean | undefined>
   */
  async sendMessageWithStreaming(
    content: string,
    chatRole?: ChatRole,
    sessionId?: string | number
  ): Promise<boolean | undefined> {
    // The param sessionId is needed for avoiding race conditions/use case when a user 
    // switches sessions quickly.
    // This is to avoid bugs with Session 1 prompt being submitted in Session 2,
    // if a user submits a prompt in Session 1 and quickly switches to Session 2.
    const currentSessionId = sessionId ?? this.#chatSessionService.selectedSessionId();
    
    this.#selectedSessionId = currentSessionId;

    // If this session isn't already active and we already have 3 active sessions, 
    // disable submitting an input and display an alert
    if (currentSessionId && this.#multiSessionService.hasReachedMaxConcurrency() && !this.#multiSessionService.isSessionStreaming(currentSessionId)) {
      this.#multiSessionService.showMaxConcurrencyAlert();
      return false;
    }

    const sessionModel = this.#chatSessionService.chatSessions()?.find(session => session?.id === currentSessionId)?.model;
    
    const isModelAvailable = await firstValueFrom(this.#utilitiesService.isModelAvailable(sessionModel!));

    // if selected model is not available, we prevent sending a message and display an alert to notify the user to select a different model.
    if (!isModelAvailable) {
      this.#utilitiesService.addModelUnavailableSystemMessage(sessionModel!);
      return;
    }

    // Mark this session as the actively submitting session
    // We'll set up the full session state after we have the subscription and abort controller

    try {
      const accessToken = this.#authService.accessToken();
      if (!accessToken) {
        this.#authService.signIn();
        return;
      }

      const currentUserId = this.#profileService.userProfile()?.profile.name!;
      const selectedSessionId = currentSessionId || this.#chatSessionService.selectedSession()?.id!;
      const maximumMessagesToUse = this.#profileService.userProfile()?.config?.maximumMessagesToUse!;

      const meta = this.#chatDocumentService.selectedDocuments().length > 0
        ? { documents: this.#chatDocumentService.selectedDocuments() }
        : null;

      this.#chatDocumentService.storeSelectedDocuments(
        String(selectedSessionId),
        this.#chatDocumentService.selectedDocuments()
      );

      // Add user message
      const userMsgParams = {
        senderId: currentUserId,
        role: chatRole ?? ChatRole.User,
        content,
        canSubmit: true,
        canCopy: content !== 'Please continue',
        meta: meta,
        currentUserId
      };

      // if we are in the active session add the message to the session and UI,
      // else, just add it to that particular session's messages array.
      if (this.#chatSessionService.selectedSessionId() == selectedSessionId) {
        this.#chatMessageService.addMessage(userMsgParams);
        // Sync to session object immediately
        const owningSession = this.#chatSessionService.selectedSession();
        if (owningSession?.id === selectedSessionId) {
           owningSession.messages = this.#chatMessageService.messages() || [];
        }
      } else {
        const newMsg = this.#chatMessageService.createMessage(userMsgParams);
        const session = this.#chatSessionService.chatSessions()?.find(s => s.id === selectedSessionId);
        if (session) {
           session.messages = session.messages || [];
           session.messages.push(newMsg);
        }
      }

      // Check if we are in the middle of a file upload workflow instruction
      if (this.#automationStateService.isFileUploadInProgress()) {
        const instruction = this.#automationStateService.instructionInProgress();
        await this.#automationExecutionService.updateChatSessionConfiguration(instruction!);
      }

      // Handle instruction-based prompts
      const currentInputData = selectedSessionId
        ? this.#chatSessionService.getSessionInputData(selectedSessionId)
        : null;

      const instructions = currentInputData?.instruction;

      if (instructions) {
        if (currentInputData?.instruction) {
          this.#automationStateService.instructionInProgress.set(currentInputData.instruction);
        }

        await this.#promptLibraryService.updateChatSessionWithSelectedPrompt(instructions);
      }

      // Build prompt messages from the correct session's history
      const sessionForPrompt = this.#chatSessionService.chatSessions()?.find(s => s.id === selectedSessionId);
      const sessionMessages = sessionForPrompt?.messages || []; 
      
      const promptMessages = [
        { role: ChatRole.System, content: AI_SYSTEM_MESSAGES.currentSystemMessage },
        ...sessionMessages
          .filter((msg) => msg.senderId === currentUserId || msg.canSubmit)
          .slice(-maximumMessagesToUse)
          .map((msg) => ({ role: msg.role, content: msg.clipboardVersion, meta: msg.meta })),
      ];

      // Add the assistant message with loading state
      const assistantMsgParams = {
        senderId: ChatRole.Assistant,
        role: ChatRole.Assistant,
        content: '',
        useLoadingMessage: true,
        currentUserId
      };

      let messageId = '';
      
      if (this.#chatSessionService.selectedSessionId() == selectedSessionId) {
        const assistantMsg = this.#chatMessageService.addMessage(assistantMsgParams);
        messageId = assistantMsg.id!;
        // Sync to session object
        const owningSession = this.#chatSessionService.selectedSession();
        if (owningSession?.id === selectedSessionId) {
           owningSession.messages = this.#chatMessageService.messages() || [];
        }
      } else {
        const assistantMsg = this.#chatMessageService.createMessage(assistantMsgParams);
        messageId = assistantMsg.id!;
        const session = this.#chatSessionService.chatSessions()?.find(s => s.id === selectedSessionId);
        if (session) {
           session.messages = session.messages || [];
           session.messages.push(assistantMsg);
        }
      }
      
      // Set session status to processing
      this.#uiLayoutService.updateSessionStatusIcon(selectedSessionId, SessionStatus.PROCESSING);

      // Persist session before streaming
      // Ensure we persist the session object that actually has the new messages
      const sessionToPersist = this.#chatSessionService.chatSessions()?.find(s => s.id === selectedSessionId);
      if (sessionToPersist) {
         await this.#apiService.persistSession(sessionToPersist).toPromise();
      }

      // Create a new AbortController for this request
      const abortController = new AbortController();
      
      // Subscribe to the streaming response with abort signal
      const subscription = this.#apiService.streamChatWithEvents({
        messageId: messageId,
        messages: promptMessages,
        sessionId: String(selectedSessionId)
      }, abortController.signal).subscribe({
        next: (data: { content: string; requestDetail?: any }) => {
          // If we are on the current session being processed, update the UI
          if (this.#chatSessionService.selectedSessionId() == selectedSessionId) {
            this.#chatMessageService.updateMessageStream(
              messageId, 
              data.content, 
              false, 
              data.requestDetail
            );
          } else {
            // else, update the background session
            this.#updateBackgroundSessionMessage(
              selectedSessionId, 
              messageId, 
              data.content, 
              false, 
              data.requestDetail
            );
          }
        },
        error: (error) => {
          // Check if error is due to abort
          if (error.name === 'AbortError') {
            return; // Don't show error message for user cancellations
          }
          this.#handleStreamError(error, content, meta, selectedSessionId);
        },
        complete: () => {
          if (this.#chatSessionService.selectedSessionId() == selectedSessionId) {
            const finalMessage = this.#chatMessageService.messages()?.find(
              msg => msg.id === messageId
            );

            if (finalMessage) {
              this.#chatMessageService.completeMessageStream(
                messageId,
                finalMessage.formatted || '',
                finalMessage.requestDetail
              );
            }
          } else {
            // Retrieve latest state from the session object
            const session = this.#chatSessionService.chatSessions()?.find(s => s.id === selectedSessionId);
            const message = session?.messages?.find(m => m.id === messageId);
            
            if (message) {
               this.#updateBackgroundSessionMessage(
                 selectedSessionId, 
                 messageId, 
                 message.formatted || '', 
                 true, 
                 message.requestDetail
               );
            }
          }

          this.#handleStreamComplete(selectedSessionId, messageId);
        }
      });
      
      // Now that we have the subscription and abort controller, properly start the session
      this.#multiSessionService.startSession(selectedSessionId, subscription, abortController, messageId, meta);
      
      return true;
    } catch (error) {
      this.#handleStreamError(error, content, this.#multiSessionService.getSessionMeta(currentSessionId!), currentSessionId);
      return false;
    }
  }



  /**
   * Handles stream completion.
   * Saves the session with the streamed message to persist chat history.
   */
  async #handleStreamComplete(sessionId: string | number, messageId: string): Promise<void> {
    // Clean up session using multi-session service
    this.#multiSessionService.completeSession(sessionId, ExecutionStatusEnum.Success);

    // Update the selected session with the new messages
    // We need to be careful here if the selected session has changed
    const currentSelectedSession = this.#chatSessionService.selectedSession();

    if (currentSelectedSession && currentSelectedSession.id === sessionId) {
      const messages = this.#chatMessageService.messages();
      if (messages) {
        // Ensure the session messages array exists
        if (!currentSelectedSession.messages) {
          currentSelectedSession.messages = [];
        }
        // Update messages ref
        currentSelectedSession.messages = messages;
      }
    }
      
    // Persist the session with the streamed messages (for both active and background sessions)
    // The background session object was updated in real-time by #updateBackgroundSessionMessage
    const sessionToPersist = this.#chatSessionService.chatSessions()?.find(s => s.id === sessionId);
    
    if (sessionToPersist) {
      try {
        await firstValueFrom(this.#apiService.persistSession(sessionToPersist));
      } catch (error) {
        this.#alertService?.showAlert({
          message: 'Failed to save chat session',
          type: AlertEnum.error
        });
      }
    }

    // Clear documents and input data on successful completion
    this.#chatDocumentService.selectedDocuments.set([]);
    this.#resetValuesForSessionInputData(sessionId);

    // Clear instruction in progress
    const hasInstruction = this.#automationStateService.instructionInProgress();
    if (hasInstruction && currentSelectedSession?.id === sessionId) {
      await this.#promptLibraryService.removeSelectedPromptForCurrentSession();
      this.#automationStateService.instructionInProgress.set(null);
    }
  }

  /**
   * Handles streaming errors.
   */
  #handleStreamError(err: any, content?: string, meta?: any, selectedSessionId?: string | number): void {
    this.#uiLayoutService.updateSessionStatusIcon(selectedSessionId!, SessionStatus.ERROR);
    

    if (selectedSessionId) {
      this.#multiSessionService.errorSession(selectedSessionId);
      
      // Persist the session with error status
      const sessionToPersist = this.#chatSessionService.chatSessions()?.find(s => s.id === selectedSessionId);
      if (sessionToPersist) {
        // Update session object with lastStatus
        this.#chatSessionService.chatSessions.update(sessions => {
          if (!sessions) return sessions;
          return sessions.map(s => 
            s.id === sessionToPersist.id 
              ? { ...s, lastStatus: SessionStatus.ERROR }
              : s
          );
        });
      }
    }

    const isAuthError = err?.status === 401 || err?.status === 403;

    if (isAuthError) {
      this.#chatMessageService.addMessage({
        senderId: ChatRole.System,
        role: ChatRole.System,
        content: GENERIC_ERROR_MESSAGE,
        system: true,
        error: true,
        currentUserId: this.#profileService.userProfile()?.profile.name!,
        showExpansionPanel: true,
        expansionPanelTitle: 'Error Processing Prompt',
      });
      this.#router.navigate([RoutePath.SignIn]);
      return;
    }

    // Add error message
    this.#chatMessageService.addMessage({
      senderId: ChatRole.System,
      role: ChatRole.System,
      content: GENERIC_ERROR_MESSAGE,
      system: true,
      error: true,
      currentUserId: this.#profileService.userProfile()?.profile.name!,
      showExpansionPanel: true,
      expansionPanelTitle: 'Error Processing Prompt',
    });

    // Restore session config if needed
    if (this.#automationStateService.instructionInProgress() &&
      this.#chatSessionService.selectedSession()?.snapshotConfiguration) {
      this.#chatUtilitiesService.restoreSessionConfig(true);
    }

    // Restore the user message to the input area
    if (content && content !== 'Please continue' && selectedSessionId) {
      const currentInputData = this.#chatSessionService.getSessionInputData(selectedSessionId);
      this.#chatSessionService.setSessionInputData(selectedSessionId, {
        text: content,
        documents: meta?.documents ?? [],
        instruction: currentInputData.instruction
      });
      
      // Only restore documents if we're in the same session
      if (this.#chatSessionService.selectedSessionId() == selectedSessionId) {
         this.#chatDocumentService.selectedDocuments.set(meta?.documents ?? []);
      }
      this.#chatDocumentService.storeSelectedDocuments(String(selectedSessionId), meta?.documents ?? []);
    }
  }

  /**
   * Cancels the current message stream and restores the user prompt.
   */
  async cancelStreamingMessage(): Promise<void> {
    const sessionId = this.#chatSessionService.selectedSession()?.id;
    if (!sessionId) return;

    // Check if we are streaming for this session
    if (!this.#multiSessionService.isSessionStreaming(sessionId)) return;

    // Capture the streaming message id before clearing multi-session state
    const messageId = this.#multiSessionService.getSessionMessageId(sessionId);
    if (!messageId) return;

    // Cancel the session using multi-session service this will also set the session icon to DEFAULT
    this.#multiSessionService.cancelSession(sessionId);

    // Find the last user message and restore it
    const lastUserMessage = this.#chatMessageService.messages()
      ?.slice()
      .reverse()
      .find(o => o.role === ChatRole.User);

    if (lastUserMessage) {
      const currentInputData = this.#chatSessionService.getSessionInputData(sessionId);
      const documentsToRestore = lastUserMessage.meta?.documents ?? [];

      this.#chatSessionService.setSessionInputData(sessionId, {
        text: lastUserMessage.clipboardVersion ?? lastUserMessage.content ?? '',
        documents: documentsToRestore,
        instruction: currentInputData.instruction
      });

      this.#chatDocumentService.selectedDocuments.set(documentsToRestore);
      this.#chatDocumentService.storeSelectedDocuments(String(sessionId), documentsToRestore);
    }

    // Restore session config if needed
    if (this.#automationStateService.instructionInProgress() &&
      this.#chatSessionService.selectedSession()?.snapshotConfiguration) {
      this.#chatUtilitiesService.restoreSessionConfig();
    }


    // B-6798
    // Remove the last two messages (the assistant message with loading state and the user message) from the session,
    // Then persist the session with the cleaned messages (without the cancelled user and assistant messages)
    const session = this.#chatSessionService.selectedSession();
    if (session) {
      session.messages = session?.messages?.filter(m => m.id !== messageId && m.id !== lastUserMessage?.id) ?? [];
      
      try {
        await firstValueFrom(this.#apiService.persistSession(session));
        // Good path: Update the messages signal to reflect the changes in the UI
        this.#chatMessageService.messages.set(session.messages);
      } catch (error) {
        // Bad path: Display an alert if persisting the session fails
        this.#alertService.showAlert({
          message: 'Failed to cancel the message. Please try again.',
          type: AlertEnum.error,
        });
      }
    }
  }

  /**
   * Resets session input data after completion.
   */
  #resetValuesForSessionInputData(sessionId: string | number): void {
    this.#chatSessionService.setSessionInputData(sessionId, {
      text: '',
      documents: [],
      instruction: undefined
    });
  }

  /**
   * Checks if a stream is currently active.
   */
  isStreaming(): boolean {
    return this.#multiSessionService.isAnySessionStreaming();
  }

  /**
   * Helper method to update message state in a background session.
   * This ensures that when the user switches back and forth between the different sessions, the state is correct.
   */
  #updateBackgroundSessionMessage(
    sessionId: string | number,
    messageId: string,
    content: string,
    isComplete: boolean,
    requestDetail?: any
  ): void {
    const session = this.#chatSessionService.chatSessions()?.find(s => s.id === sessionId);
    if (!session || !session.messages) return;

    const message = session.messages.find(m => m.id === messageId);
    if (message) {
      message.formatted = content;
      message.clipboardVersion = content;
      message.isStreaming = !isComplete;
      message.useLoadingMessage = !isComplete;
      message.canSubmit = isComplete;
      message.canCopy = isComplete;
      if (requestDetail) {
        message.requestDetail = requestDetail;
      }
    }
  }
}
